import { Outlet, useLocation, useNavigate, matchPath, ScrollRestoration, useMatch, useParams } from 'react-router-dom'
import { App } from '@capacitor/app'
import { BottomNavigation, BottomNavigationAction, Divider, List, useMediaQuery, useTheme, Alert, IconButton, Tooltip } from '@mui/material'
import AttributionIcon from '@mui/icons-material/Attribution';
import styles from './styles.module.scss'
import logo from '../../assets/logo.png';
import circleLogo from '../../assets/logo_circle.png';
import StudentNav, { MobileStudentHeader } from './components/studentnav'
import { NavItem } from './components'


import { useEffect, useRef, useState } from 'react'
import { useApp, useSettings, NotificationContextProvider, DaymapIcon, DaymapIconType, useAuth, PushNotificationSnackbars, QuickActionMenu } from 'daymap-react'
import { Announcements} from '../../components/announcements';
import { MoreDrawer } from './components/more';
import { PluginListenerHandle } from '@capacitor/core';
import { useInAppReview } from '../../components/appreview';
import { getNotificationUrl } from '../../pages/home/components/notifications';

export type MainMenuItem = {
    to: string;
    label: string;
    iconType: DaymapIconType;
    showUnderMoreOnMobile?: boolean;
    HideFromNavBar?: boolean;
    HideFromBottom?: boolean;
}

const defaultMenus:MainMenuItem[] = [
    { to: "/", label: "Home", iconType: DaymapIconType.Home },
    { to: "/messaging", label: "Communication", iconType: DaymapIconType.Message },
    { to: "/calendar", label: "Calendar", iconType: DaymapIconType.Calendar},
    { to: "/more", label: "More", iconType: DaymapIconType.More },
]

function MainNavigationLayout() {
    const md = useMediaQuery((theme) => theme.breakpoints.up('md')); // 900px
    const navigate = useNavigate();
    const location = useLocation();
    const mainElRef = useRef<HTMLDivElement>(null);
    const { setMainEl } = useApp();
    const {IsImpersonating, Unimpersonate} = useAuth();
    const theme = useTheme();
    const minimized = useMatch("/student/:studentId/*");
    const { studentId } = useParams<{ studentId?: string }>();
    const {settings} = useSettings();
    const [menus, setMenus] = useState(defaultMenus);
    const [moreOpen, setMoreOpen] = useState(false);
    const [quickActionMenuOpen, setQuickActionMenuOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    useInAppReview();

    useEffect(() => {
        let handler:PluginListenerHandle;
        const setup = async () => {
            handler = await App.addListener('backButton', () => {
                if (moreOpen)
                    setMoreOpen(false);
                else if (location.pathname === '/')
                    App.exitApp();
                else
                    navigate(-1);
            });
        }
        setup();
        return () => {
            handler?.remove();
        }
    }, [moreOpen, location])
    
    useEffect(() => {
        mainElRef.current && setMainEl(mainElRef.current)
    }, [mainElRef]);

    useEffect(() => {
        if(!settings) {return;}
        const temp:MainMenuItem[] = [
            { to: "/", label: "Home", iconType: DaymapIconType.Home },
            { to: "/messaging", label: "Communication", iconType: DaymapIconType.Message },
            { to: "/calendar", label: "Calendar", iconType: DaymapIconType.Calendar},

        ]
        if(settings.hasPayments && !IsImpersonating){
            temp.push({ to: "/payments", label: "Payments", iconType: DaymapIconType.Payment, showUnderMoreOnMobile: true })
        }

        temp.push({ to: "/mydetails", label: "My Details", iconType: DaymapIconType.User, showUnderMoreOnMobile: true });
        temp.push({ to: "/more", label: "More", iconType: DaymapIconType.More},);

        if (location.pathname === '/' && settings.quickActionList && settings.quickActionList.length > 0)
        {
            temp[0].HideFromBottom = true;
            temp.splice(1, 0, { to: "/quickactions", label: "Quick Action", iconType: DaymapIconType.QuickAction, HideFromNavBar: true });
        }

        setMenus(temp);
    }, [settings, location.pathname]);

    const handleNavChange = (_: React.SyntheticEvent, value: any) => {
        if (value === '/more')
            setMoreOpen(true);
        else if (value === '/quickactions')
        {
            setAnchorEl(_.currentTarget as HTMLElement);
            setQuickActionMenuOpen(true);
        }
        else
            navigate(`${value}`)
    }

    return (
        <>
            <MoreDrawer open={moreOpen} menus={menus.filter(m => !md && m.showUnderMoreOnMobile)} onClose={() => setMoreOpen(false)} />
            <QuickActionMenu anchorEl={anchorEl} open={quickActionMenuOpen} onClose={() => setQuickActionMenuOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}/>
            <PushNotificationSnackbars getNotificationUrl={getNotificationUrl} />
            <Announcements />
            <NotificationContextProvider>
                <ScrollRestoration />
                <div className={styles.main}>
                    <div className={styles.container} ref={mainElRef}>
                        {md && <>
                                <div className={styles.leftNavContainer}>
                                    <div className={styles.leftNav}>
                                        <div className={styles.nav}>
                                            <div role="img" style={{
                                                backgroundImage: `url('${minimized ? circleLogo : logo}')`,
                                                backgroundSize: "contain",
                                                backgroundPosition: "center",
                                                backgroundRepeat: "no-repeat",
                                                width: minimized ? "40px" : "150px",
                                                height: "60px",
                                                transition: "background-image .15s",
                                                marginBottom: '17px',
                                            }}></div>
                                            {IsImpersonating && !minimized &&
                                                <Alert sx={{paddingTop: 0, 
                                                        paddingBottom: 0, 
                                                        cursor: "pointer",
                                                    }} 
                                                    severity="warning" 
                                                    variant="filled" 
                                                    icon={<AttributionIcon />}
                                                    onClick={() => Unimpersonate()}
                                                >
                                                    Stop Impersonating
                                                </Alert>
                                            }
                                            {IsImpersonating && minimized && 
                                                <Tooltip title="Stop Impersonating">
                                                    <IconButton aria-label="Stop Impersonating" onClick={() => Unimpersonate()}>
                                                        <AttributionIcon sx={{ color: theme => theme.palette.warning.main }}/>
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                            <List component="nav">
                                                {menus.filter(m => !m.HideFromNavBar).map(m => <NavItem 
                                                    key={m.label} 
                                                    title={m.label} 
                                                    iconType={m.iconType} 
                                                    to={m.to} 
                                                    selected={Boolean(matchPath(location.pathname, m.to))}
                                                    minimized={Boolean(minimized)}
                                                    onClick={m.to === "/more" ? () => setMoreOpen(true) : undefined} />)}
                                            </List>
                                        </div>
                                    </div>
                                </div>
                                <StudentNav className={styles.studentNav} studentId={studentId ? Number(studentId) : undefined} />
                            </>
                        }
                        <main>
                            {!md && studentId && <MobileStudentHeader studentId={Number(studentId)} />}
                            <Outlet />
                        </main>
                    </div>
                    {!md &&
                        <nav style={{ backgroundColor: theme.palette.background.paper, paddingBottom: 'env(safe-area-inset-bottom)' }}>
                            <Divider />
                            <BottomNavigation value={location.pathname} onChange={handleNavChange} color="primary">
                                {menus.filter(m => !md && !m.showUnderMoreOnMobile && !m.HideFromBottom).map(m => <BottomNavigationAction key={m.label} value={m.to} icon={<DaymapIcon iconType={m.iconType} />} />)}
                            </BottomNavigation>
                        </nav>
                    }
                </div>
            </NotificationContextProvider>
        </>
    )
}

export default MainNavigationLayout